import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class DateRangePickerService {

  private isSyncing = false;
  private startDate: Date | null = null;
  private tooltipElement: HTMLElement | null = null;
  /**
   * The DaterangepickerComponent instance, exposed via the directive's
   * `picker` property. Once set, alignToStartDate can write to
   * leftCalendar.month and rightCalendar.month directly instead of clicking
   * the prev/next buttons (which trigger a CSS animation per click).
   */
  private pickerInstance: any = null;

  /**
   * Configura la sincronización del calendario y el hover counter
   */
setup(pickerElement: HTMLElement): void {
    if (!pickerElement) {
        return;
    }

    this.addCalendarNavigationListeners(pickerElement);
    this.setupPrevButtonObserver(pickerElement);
    this.setupHoverCounter(pickerElement);
}

  /**
   * Configura el contador de días durante el hover
   */
  private setupHoverCounter(pickerElement: HTMLElement): void {
    // Escuchar cuando se selecciona una fecha de inicio
    this.observeStartDateSelection(pickerElement);

    // Agregar listeners a todas las celdas para hover
    this.addHoverListenersToCells(pickerElement);

    // Observar cambios en el DOM (cuando cambia el mes)
    const observer = new MutationObserver(() => {
      this.addHoverListenersToCells(pickerElement);
    });

    observer.observe(pickerElement, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Observa cuándo se selecciona la fecha de inicio
   */
private observeStartDateSelection(pickerElement: HTMLElement): void {

    // Escuchar clicks en todas las celdas
    pickerElement.addEventListener('click', (e: Event) => {
        const target = e.target as HTMLElement;
        const cell = target.closest('td.available');

        if (cell) {

            // Después del click, esperar a que Angular actualice las clases
            setTimeout(() => {
                this.updateStartDate(pickerElement);
            }, 100);
        }
    });

    // También escuchar cambios en el modelo (si está disponible)
    const applyBtn = pickerElement.querySelector('.applyBtn');
    if (applyBtn) {
        applyBtn.addEventListener('click', () => {
            this.clearStartDate();
        });
    }
}

  /**
   * Actualiza la fecha de inicio desde el calendario
   */
private updateStartDate(pickerElement: HTMLElement): void {

    // Buscar la fecha de inicio de diferentes maneras
    let activeStart = pickerElement.querySelector('td.active.start-date');

    // Si no encuentra, buscar td.active (a veces no tiene start-date)
    if (!activeStart) {
        activeStart = pickerElement.querySelector('td.active');
    }

    // Si aún no encuentra, buscar el primer día seleccionado
    if (!activeStart) {
        const allActive = pickerElement.querySelectorAll('td.active');
        if (allActive.length > 0) {
            activeStart = allActive[0];
        }
    }

    if (activeStart) {
        // Intentar obtener la fecha de diferentes atributos
        let dateAttr = activeStart.getAttribute('data-title') ||
                       activeStart.getAttribute('data-date') ||
                       activeStart.getAttribute('data-value') ||
                       activeStart.textContent;

        if (dateAttr) {
            // Determinar qué calendario (izquierdo o derecho)
            const parentCalendar = activeStart.closest('.calendar');
            let currentMonth = '';

            if (parentCalendar) {
                const monthElement = parentCalendar.querySelector('th.month');
                currentMonth = monthElement?.textContent || '';
            } else {
                // Fallback: usar el izquierdo
                currentMonth = pickerElement.querySelector('.calendar.left th.month')?.textContent || '';
            }


            if (currentMonth) {
                const parsedDate = this.parseDateFromCell(dateAttr, currentMonth);

                if (parsedDate && !isNaN(parsedDate.getTime())) {
                    this.startDate = parsedDate;
                }
            }
        }
    } else {
        // Verificar si es un nuevo rango (ambas fechas seleccionadas)
        const startDateElem = pickerElement.querySelector('td.start-date');
        const endDateElem = pickerElement.querySelector('td.end-date');

        if (startDateElem && endDateElem) {
            // No limpiamos startDate para mantenerlo
        } else {
            this.clearStartDate();
        }
    }
}

  /**
   * Agrega listeners de hover a todas las celdas
   */
private addHoverListenersToCells(pickerElement: HTMLElement): void {
    const cells = pickerElement.querySelectorAll('td.available');

    cells.forEach((cell: Element) => {
      const htmlCell = cell as HTMLElement;

      if (!htmlCell.hasAttribute('data-hover-listener')) {
        htmlCell.setAttribute('data-hover-listener', 'true');

        htmlCell.addEventListener('mouseenter', (e) => {
          this.onCellHover(htmlCell, pickerElement);
        });

        htmlCell.addEventListener('mouseleave', () => {
          this.hideTooltip();
        });
      }
    });
}

  /**
   * Maneja el hover sobre una celda
   */
private onCellHover(cell: HTMLElement, pickerElement: HTMLElement): void {

    if (!this.startDate) {
        return;
    }

    // ✅ VERIFICAR SI LA CELDA ESTÁ DESHABILITADA
    const isDisabled = cell.classList.contains('disabled') ||
                       cell.classList.contains('off') ||
                       cell.classList.contains('unavailable') ||
                       cell.classList.contains('inactive') ||
                       cell.getAttribute('aria-disabled') === 'true';

    if (isDisabled) {
        return;
    }
    // También verificar si tiene clase 'off' (meses fuera del rango)
    if (cell.classList.contains('off')) {
        return;
    }

    // Normalizar startDate
    let startDateNormalized: Date | null = null;

    if (this.startDate instanceof Date && !isNaN(this.startDate.getTime())) {
        startDateNormalized = this.startDate;
    } else if (this.startDate && typeof this.startDate === 'object' && '$isDayjsObject' in this.startDate) {
        const dayjsObj = this.startDate as any;
        if (dayjsObj.$isDayjsObject && dayjsObj.$d instanceof Date) {
            startDateNormalized = new Date(dayjsObj.$d);
        } else if (dayjsObj.toDate && typeof dayjsObj.toDate === 'function') {
            startDateNormalized = dayjsObj.toDate();
        }
    }

    if (!startDateNormalized) {
        return;
    }

    // Evitar si la celda es la fecha de inicio
    if (cell.classList.contains('start-date')) {
        return;
    }

    // Obtener la fecha de la celda hover
    const hoverDate = this.getDateFromCell(cell, pickerElement);

    if (!hoverDate) {
        return;
    }

    // Calcular días de diferencia
    const start = new Date(startDateNormalized);
    const end = new Date(hoverDate);

    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    // ✅ NUEVO: NO mostrar tooltip si la fecha hover es ANTERIOR a la fecha de inicio
    if (end.getTime() < start.getTime()) {
        return;
    }

    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    // Solo mostrar si los días son válidos y la celda está habilitada
    if (diffDays > 0 && !isNaN(diffDays)) {
        this.showTooltip(cell, `${diffDays} día${diffDays !== 1 ? 's' : ''}`);
    }
}
  /**
   * Obtiene la fecha de una celda
   */
private getDateFromCell(cell: HTMLElement, pickerElement: HTMLElement): Date | null {

    // Intentar obtener de diferentes atributos
    let dateStr = cell.getAttribute('data-title') ||
                  cell.getAttribute('data-date') ||
                  cell.getAttribute('data-value') ||
                  cell.textContent;


    if (!dateStr) return null;

    // Obtener el mes actual del calendario (izquierdo o derecho)
    const parentCalendar = cell.closest('.calendar');

    let monthHeader = '';
    if (parentCalendar) {
        const monthElement = parentCalendar.querySelector('th.month');
        monthHeader = monthElement?.textContent || '';
    } else {
        monthHeader = pickerElement.querySelector('.calendar.left th.month')?.textContent || '';
    }


    if (monthHeader) {
        const result = this.parseDateFromCell(dateStr, monthHeader);
        return result;
    }

    return null;
}
  /**
   * Parsea fecha desde texto de celda y mes
   */
private parseDateFromCell(dayStr: string, monthHeader: string): Date | null {

    try {
        // Limpiar el día (remover cualquier texto no numérico)
        const day = parseInt(dayStr.replace(/\D/g, ''), 10);
        if (isNaN(day)) {
            return null;
        }


        // Parsear mes y año del header
        // El header puede ser "May 2026", "Mayo 2026", "May  2026" (con espacios múltiples)
        const cleanedHeader = monthHeader.trim().replace(/\s+/g, ' ');
        const monthParts = cleanedHeader.split(' ');


        if (monthParts.length < 2) {
            return null;
        }

        // Determinar si el año viene primero o después
        let monthName: string;
        let year: number;

        // Si la primera parte es un número (ej: "2026 May")
        if (/^\d+$/.test(monthParts[0])) {
            year = parseInt(monthParts[0], 10);
            monthName = monthParts[1];
        }
        // Si la segunda parte es un número (ej: "May 2026")
        else if (/^\d+$/.test(monthParts[1])) {
            monthName = monthParts[0];
            year = parseInt(monthParts[1], 10);
        }
        else {
            return null;
        }


        const month = this.getMonthNumber(monthName);

        if (isNaN(month) || isNaN(year)) {
            return null;
        }

        const date = new Date(year, month, day);

        // Validar que la fecha sea válida
        if (isNaN(date.getTime())) {
            return null;
        }

        return date;
    } catch (e) {
        return null;
    }
}
  /**
   * Muestra el tooltip con los días
   */
private showTooltip(cell: HTMLElement, message: string): void {

    this.hideTooltip();

    const rect = cell.getBoundingClientRect();

    this.tooltipElement = document.createElement('div');
    this.tooltipElement.className = 'daterangepicker-hover-tooltip';
    this.tooltipElement.textContent = message;
    this.tooltipElement.style.cssText = `
        position: fixed;
        top: ${rect.top - 35}px;
        left: ${rect.left + rect.width / 2}px;
        transform: translateX(-50%);
        background: #333;
        color: white;
        font-size: 12px;
        font-weight: 500;
        padding: 5px 10px;
        border-radius: 4px;
        pointer-events: none;
        z-index: 100000;
        white-space: nowrap;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    `;

    // Agregar flecha
    const arrow = document.createElement('div');
    arrow.style.cssText = `
        position: absolute;
        bottom: -6px;
        left: 50%;
        transform: translateX(-50%);
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 6px solid #333;
    `;
    this.tooltipElement.appendChild(arrow);

    document.body.appendChild(this.tooltipElement);
}
  /**
   * Oculta el tooltip
   */
  private hideTooltip(): void {
    if (this.tooltipElement) {
      this.tooltipElement.remove();
      this.tooltipElement = null;
    }
  }

  // Mantener sincronización existente
  private setupPrevButtonObserver(pickerElement: HTMLElement): void {
    const observer = new MutationObserver(() => {
      const leftPrev = pickerElement.querySelector('.calendar.left .prev');
      if (leftPrev && !leftPrev.hasAttribute('data-listener-added')) {
        this.addCalendarNavigationListeners(pickerElement);
      }
    });

    observer.observe(pickerElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private addCalendarNavigationListeners(pickerElement: HTMLElement): void {
    const leftPrev = pickerElement.querySelector('.calendar.left .prev');
    const rightNext = pickerElement.querySelector('.calendar.right .next');

    if (leftPrev && !leftPrev.hasAttribute('data-listener-added')) {
      leftPrev.setAttribute('data-listener-added', 'true');
      leftPrev.addEventListener('click', () => this.handleLeftPrev(pickerElement));
    }

    if (rightNext && !rightNext.hasAttribute('data-listener-added')) {
      rightNext.setAttribute('data-listener-added', 'true');
      rightNext.addEventListener('click', () => this.handleRightNext(pickerElement));
    }
  }

private handleLeftPrev(pickerElement: HTMLElement): void {
        if (this.isSyncing) return;
        if (!this.pickerInstance) {
            // Fallback to DOM-reading path if we don't have the picker instance
            this.handleLeftPrevLegacy(pickerElement);
            return;
        }
        this.isSyncing = true;
        // Read directly from the picker's internal state — avoids the
        // fragile DOM-text parsing that breaks during animations.
        const leftMonth = this.pickerInstance.leftCalendar.month;
        if (leftMonth) {
            this.pickerInstance.rightCalendar.month = leftMonth.clone().add(1, 'month');
            // IMPORTANT: call updateCalendars() (NOT updateView()) here.
            // updateView() runs updateMonthsInView() which resets both
            // calendars back to the startDate, undoing the user's
            // navigation. updateCalendars() just re-renders the day grid.
            this.pickerInstance.updateCalendars();
        }
        // Hold the lock long enough to absorb a fast double-click on the
        // prev button. Otherwise two clicks in <100ms both fire handlers
        // and the second one reads a stale state.
        setTimeout(() => { this.isSyncing = false; }, 200);
    }

    private handleRightNext(pickerElement: HTMLElement): void {
        if (this.isSyncing) return;
        if (!this.pickerInstance) {
            this.handleRightNextLegacy(pickerElement);
            return;
        }
        this.isSyncing = true;
        const rightMonth = this.pickerInstance.rightCalendar.month;
        if (rightMonth) {
            this.pickerInstance.leftCalendar.month = rightMonth.clone().subtract(1, 'month');
            this.pickerInstance.updateCalendars();
        }
        setTimeout(() => { this.isSyncing = false; }, 200);
    }

    // Legacy paths kept as a safety net in case setPicker hasn't run yet
    // (e.g. an old build of the host that hasn't been updated).
    private handleLeftPrevLegacy(pickerElement: HTMLElement): void {
        this.isSyncing = true;
        setTimeout(() => {
            const leftMonthText = pickerElement.querySelector('.calendar.left th.month')?.textContent?.trim() || '';
            const leftDate = this.parseMonthText(leftMonthText);
            if (leftDate) {
                const targetRightDate = new Date(leftDate.getFullYear(), leftDate.getMonth() + 1, 1);
                this.syncCalendarTo(pickerElement, 'right', targetRightDate);
            }
            this.isSyncing = false;
        }, 100);
    }

    private handleRightNextLegacy(pickerElement: HTMLElement): void {
        this.isSyncing = true;
        setTimeout(() => {
            const rightMonthText = pickerElement.querySelector('.calendar.right th.month')?.textContent?.trim() || '';
            const rightDate = this.parseMonthText(rightMonthText);
            if (rightDate) {
                const targetLeftDate = new Date(rightDate.getFullYear(), rightDate.getMonth() - 1, 1);
                this.syncCalendarTo(pickerElement, 'left', targetLeftDate);
            }
            this.isSyncing = false;
        }, 100);
    }

  private syncCalendarTo(pickerElement: HTMLElement, side: 'left' | 'right', targetDate: Date): void {
    const currentMonthText = pickerElement.querySelector(`.calendar.${side} th.month`)?.textContent?.trim() || '';
    const currentDate = this.parseMonthText(currentMonthText);

    if (!currentDate) return;

    const monthsDiff = (targetDate.getFullYear() - currentDate.getFullYear()) * 12 +
                       (targetDate.getMonth() - currentDate.getMonth());

    if (monthsDiff === 0) return;

    const buttonSelector = monthsDiff > 0 ? '.next' : '.prev';
    const navButton = pickerElement.querySelector(`.calendar.${side} ${buttonSelector}`) as HTMLElement;

    if (navButton) {
      const clicks = Math.abs(monthsDiff);
      for (let i = 0; i < clicks; i++) {
        setTimeout(() => navButton.click(), i * 100);
      }
    }
  }

  private parseMonthText(monthText: string): Date | null {
    if (!monthText) return null;
    try {
      const cleanText = monthText.trim().replace(/\s+/g, ' ');
      const parts = cleanText.split(' ');
      let month: number, year: number;

      if (parts.length === 2) {
        if (/\d/.test(parts[0])) {
          year = parseInt(parts[0], 10);
          month = this.getMonthNumber(parts[1]);
        } else {
          month = this.getMonthNumber(parts[0]);
          year = parseInt(parts[1], 10);
        }

        if (!isNaN(month) && !isNaN(year)) {
          return new Date(year, month, 1);
        }
      }
    } catch (e) {
      console.warn('Error parsing month:', monthText);
    }
    return null;
  }

private getMonthNumber(monthName: string): number {
    const months: { [key: string]: number } = {
        // Inglés
        'Jan': 0, 'January': 0,
        'Feb': 1, 'February': 1,
        'Mar': 2, 'March': 2,
        'Apr': 3, 'April': 3,
        'May': 4,
        'Jun': 5, 'June': 5,
        'Jul': 6, 'July': 6,
        'Aug': 7, 'August': 7,
        'Sep': 8, 'September': 8,
        'Oct': 9, 'October': 9,
        'Nov': 10, 'November': 10,
        'Dec': 11, 'December': 11,

        // Español
        'Enero': 0, 'ene': 0,
        'Febrero': 1, 'feb': 1,
        'Marzo': 2, 'mar': 2,
        'Abril': 3, 'abr': 3,
        'Mayo': 4,
        'Junio': 5, 'jun': 5,
        'Julio': 6, 'jul': 6,
        'Agosto': 7, 'ago': 7,
        'Septiembre': 8, 'sept': 8, 'set': 8,
        'Octubre': 9, 'oct': 9,
        'Noviembre': 10, 'nov': 10,
        'Diciembre': 11, 'dic': 11
    };

    const normalized = monthName.trim().charAt(0).toUpperCase() + monthName.trim().slice(1).toLowerCase();
    const result = months[normalized] ?? months[monthName] ?? -1;
    return result;
}

      /**
     * Método público para establecer la fecha de inicio desde el componente
     */
// En date-range-picker.service.ts
setStartDate(date: any): void {

        let validDate: Date | null = null;

        // Intentar convertir a Date usando diferentes métodos
        try {
            // Si es Day.js y tiene método toDate()
            if (date && typeof date.toDate === 'function') {
                validDate = date.toDate();
            }
            // Si es Day.js y tiene $d
            else if (date && date.$isDayjsObject === true && date.$d) {
                validDate = new Date(date.$d);
            }
            // Si es Date nativo
            else if (date instanceof Date) {
                validDate = date;
            }
            // Si es string o número
            else if (typeof date === 'string' || typeof date === 'number') {
                validDate = new Date(date);
            }
            // Si viene dentro de startDate
            else if (date && date.startDate) {
                return this.setStartDate(date.startDate);
            }
        } catch (e) {
            console.error('Error al convertir fecha:', e);
        }

        if (validDate && !isNaN(validDate.getTime())) {
            this.startDate = validDate;
        } else {
            this.startDate = null;
        }
    }

    /**
     * Inject the DaterangepickerComponent so alignToStartDate can mutate
     * leftCalendar.month / rightCalendar.month directly, skipping the
     * animated prev/next clicks.
     */
    setPicker(picker: any): void {
        this.pickerInstance = picker;
    }

    /**
     * Same as alignToStartDate but writes to the picker's internal state
     * directly using a host-supplied dayjs constructor, then calls
     * updateView() so the DOM reflects the new months in a single render —
     * no animation, no "tour through every month".
     *
     * Pass the same dayjs module the host uses (so the objects we write
     * are dayjs instances the library understands).
     */
    alignDirectly(dayjs: any): boolean {
        if (!this.pickerInstance || !this.startDate || !dayjs) {
            return false;
        }
        const start = dayjs(this.startDate);
        this.pickerInstance.leftCalendar.month = start.clone().date(2);
        this.pickerInstance.rightCalendar.month = start.clone().date(2).add(1, 'month');
        this.pickerInstance.updateView();
        return true;
    }
    /**
     * Método público para limpiar la fecha de inicio
     */
    clearStartDate(): void {
        this.startDate = null;
        this.hideTooltip();
    }

    /**
     * Aligns the two calendars to the stored startDate. Without this,
     * ngx-daterangepicker always opens the left calendar on the current
     * month, so a Nov 15 – Nov 20 range ends up showing Jul | Aug.
     *
     * Idempotent: does nothing if the left calendar is already on the
     * correct month AND the right calendar is on startDate + 1 month.
     *
     * The picker renders asynchronously (open() → setTimeout for show
     * animation → updateView). We poll the DOM until both calendar
     * headers exist, then correct any misaligned months.
     */
alignToStartDate(pickerElement: HTMLElement): void {
        if (!pickerElement || !this.startDate) {
            return;
        }
        const startTime = Date.now();
        const maxWaitMs = 2000;

        const targetYear = this.startDate!.getFullYear();
        const targetMonth = this.startDate!.getMonth();
        const expectedRightYear = targetMonth === 11 ? targetYear + 1 : targetYear;
        const expectedRightMonth = (targetMonth + 1) % 12;

        const tryAlign = () => {
            const leftMonthText = pickerElement.querySelector('.calendar.left th.month')?.textContent?.trim() || '';
            const rightMonthText = pickerElement.querySelector('.calendar.right th.month')?.textContent?.trim() || '';
            const leftDate = this.parseMonthText(leftMonthText);
            const rightDate = this.parseMonthText(rightMonthText);

            if (!leftDate || !rightDate) {
                if (Date.now() - startTime < maxWaitMs) {
                    setTimeout(tryAlign, 50);
                }
                return;
            }

            const leftMonthsDiff =
                (targetYear - leftDate.getFullYear()) * 12 +
                (targetMonth - leftDate.getMonth());

            const rightMonthsDiff =
                (expectedRightYear - rightDate.getFullYear()) * 12 +
                (expectedRightMonth - rightDate.getMonth());

            if (leftMonthsDiff === 0 && rightMonthsDiff === 0) {
                return;
            }

            // Advance the left calendar one month at a time. Each click
            // triggers the library to re-render, so we MUST wait between
            // clicks — a tight loop leaves the DOM in an intermediate state.
            if (leftMonthsDiff !== 0) {
                const leftButton = leftMonthsDiff > 0 ? '.next' : '.prev';
                const navButton = pickerElement.querySelector(
                    `.calendar.left ${leftButton}`
                ) as HTMLElement | null;
                if (navButton) {
                    const leftClicks = Math.abs(leftMonthsDiff);
                    for (let i = 0; i < leftClicks; i++) {
                        setTimeout(() => navButton.click(), i * 80);
                    }
                }
            }

            // Same for the right calendar: count actual month distance and
            // click that many times. Using a single click (the previous fix)
            // only moved it by one month, leaving the right calendar far
            // behind the left.
            if (rightMonthsDiff !== 0) {
                const rightButton = pickerElement.querySelector(
                    rightMonthsDiff > 0 ? '.calendar.right .next' : '.calendar.right .prev'
                ) as HTMLElement | null;
                if (rightButton) {
                    const rightClicks = Math.abs(rightMonthsDiff);
                    const startDelay = Math.abs(leftMonthsDiff) * 80 + 100;
                    for (let i = 0; i < rightClicks; i++) {
                        setTimeout(() => rightButton.click(), startDelay + i * 80);
                    }
                }
            }
        };

        // Kick off the polling. The first attempt also runs after a small
        // delay because ngx-daterangepicker mounts the popup asynchronously
        // after picker.open().
        setTimeout(tryAlign, 50);
    }
}
